import {
    DashletSettingList,
    DashletSettingDropdownGrouped,
    DashletSettingsForm,
    DashletSettingsGroup,
    DashletSettingDropdown,
    DashletSettingText,
    DashletSettingDropdownGroupedList,
    SelectItemGroup
} from '@models/index';
import { DashletSettingsService } from '@services/index';
import { forkJoin, switchMap } from 'rxjs';

export class DashletSettingsFormSystemHealthSummary extends DashletSettingsForm {
    private customer!: DashletSettingDropdown;
    private location!: DashletSettingDropdown;
    private equipment!: DashletSettingDropdownGroupedList;

    constructor(private readonly settingsService: DashletSettingsService) {
        super();
        this.init();
        this.loadCustomers();
    }

    loadState(obj: { [key: string]: object }): void {
        super.loadState(obj);

        if (this.customer.value) {
            this.loadLocations(this.customer.value);
        }

        if (this.location.value) {
            this.loadEquipment(this.location.value);
        }
    }

    private init(): void {
        const equipmentGroup = new DashletSettingsGroup();
        const s: any = (equipmentGroup.settings = []);

        const customer = (this.customer = new DashletSettingDropdown());
        customer.label = 'Customer';
        customer.stateKey = 'customer';
        customer.disabled = true;
        customer.change.subscribe(() => {
            this.clearAndDisable(this.location);
            this.clearAndDisable(this.equipment);
            this.loadLocations(customer.value!);
        });
        s.push(customer);

        const location = (this.location = new DashletSettingDropdown());
        location.label = 'Location';
        location.stateKey = 'location';
        location.disabled = true;
        location.change.subscribe(() => {
            this.clearAndDisable(this.equipment);
            this.loadEquipment(location.value!);
        });
        s.push(location);

        const equipment = (this.equipment = new DashletSettingDropdownGroupedList());
        equipment.label = 'Equipment';
        equipment.stateKey = 'equipment';
        equipment.disabled = true;
        equipment.multiple = true;
        s.push(equipment);

        const customName = new DashletSettingText(40);
        customName.label = 'Custom Dashlet Name';
        customName.stateKey = 'customName';
        s.push(customName);

        const group = new DashletSettingsGroup();
        group.groups = [equipmentGroup];
        this.updateSettings(group);
    }

    private loadCustomers(): void {
        this.enableAndLoading(this.customer);
        this.settingsService.getUserAssociatedCustomers().subscribe(
            customers => {
                this.customer.items = customers;
                this.customer.loadingComplete();
            },
            () => {
                this.customer.loadingComplete();
                this.customer.disabled = true;
                // show error notification here
            }
        );
    }

    private loadLocations(customer: string): void {
        this.enableAndLoading(this.location);
        this.settingsService.getLocations(customer).subscribe(
            locations => {
                locations.unshift({ label: 'All', value: '=all=' });
                this.location.items = locations;
                this.location.loadingComplete();
            },
            () => {
                this.location.loadingComplete();
                this.location.disabled = true;
                // show error notification here
            }
        );
    }

    private loadEquipment(location: string) {
        this.enableAndLoading(this.equipment);
        this.equipment.items = [];
        if (this.location.value !== '=all=') {
            this.settingsService.getSystemHealthEquipment(location).subscribe(
                equipmentGroup => {
                    equipmentGroup.forEach(group => {
                        this.equipment.items.push(group);
                    });
                    this.equipment.loadingComplete();
                },
                () => {
                    this.equipment.loadingComplete();
                    this.equipment.disabled = true;
                }
            );
        } else {
            this.settingsService
                .getLocations(this.customer.value!)
                .pipe(
                    switchMap(locations =>
                        forkJoin(
                            locations.map(location => this.settingsService.getSystemHealthEquipment(location.value))
                        )
                    )
                )
                .subscribe({
                    next: allEquipments => {
                        allEquipments.forEach(equipmentGroup => {
                            equipmentGroup.forEach(group => {
                                this.equipment.items.push(group);
                            });
                        });

                        const groupedEquipments: SelectItemGroup[] = Object.values(
                            this.equipment.items.reduce(
                                (acc: Record<string, SelectItemGroup>, curr: SelectItemGroup) => {
                                    if (!acc[curr.label]) {
                                        acc[curr.label] = { label: curr.label, items: [] };
                                    }

                                    acc[curr.label].items.push(...curr.items);
                                    return acc;
                                },
                                {} as Record<string, SelectItemGroup>
                            )
                        ) // sort items inside each group
                            .map(group => ({
                                label: group.label,
                                items: group.items.sort((a, b) => a.label.localeCompare(b.label))
                            }))
                            // sort the groups
                            .sort((a, b) => a.label.localeCompare(b.label));

                        this.equipment.items = groupedEquipments;

                        this.equipment.loadingComplete();
                    },
                    error: () => {
                        this.equipment.loadingComplete();
                        this.equipment.disabled = true;
                    }
                });
        }
    }

    private clearAndDisable(
        dropdown:
            | DashletSettingDropdown
            | DashletSettingDropdownGrouped
            | DashletSettingList<string>
            | DashletSettingDropdownGroupedList
    ) {
        dropdown.disabled = true;
        dropdown.items = [];
        if (dropdown instanceof DashletSettingList || dropdown instanceof DashletSettingDropdownGroupedList) {
            dropdown.values = [];
        } else {
            dropdown.value = null;
        }
    }

    private enableAndLoading(
        dropdown:
            | DashletSettingDropdown
            | DashletSettingDropdownGrouped
            | DashletSettingList<string>
            | DashletSettingDropdownGroupedList
    ) {
        dropdown.disabled = false;
        dropdown.loadingBegin();
    }
}
